import { useState, useEffect } from "react";
import styles from "./ResetPasswordForm.module.scss";
import classNames from "classnames/bind";
import { IResetPasswordPayload } from "~/utils/interface/auth";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import { ArrowLeftIcon } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PasswordInput from "~/components/common/password-input/PasswordInput";
import { useAppDispatch } from "~/redux/hooks";
import { handleResetPassword, handleLogin } from "~/thunks/auth/authThunk";
import { notify } from "~/utils/helper";
import { useTranslation } from "react-i18next";
import { adminRouteAbsolute } from "~/utils/constants/route";
const cx = classNames.bind(styles);

interface IFormErrors {
  password?: string;
  confirmPassword?: string;
}

const ResetPasswordForm = () => {
  const { t } = useTranslation(["admin/auth"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get("code") || "";

  const [formData, setFormData] = useState<IResetPasswordPayload>({
    password: "",
    confirmPassword: "",
    code: codeFromUrl,
  });
  const [errors, setErrors] = useState<IFormErrors>({});
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  // Update code when URL changes
  useEffect(() => {
    const code = searchParams.get("code") || "";
    setFormData((prev) => ({
      ...prev,
      code: code,
    }));
  }, [searchParams]);

  // Validate password
  const validatePassword = (password: string): string => {
    if (!password.trim()) {
      return t("password.required");
    }
    return "";
  };

  // Validate confirm password
  const validateConfirmPassword = (
    confirmPassword: string,
    password: string
  ): string => {
    if (!confirmPassword.trim()) {
      return t("confirmPassword.required");
    }
    if (confirmPassword !== password) {
      return t("confirmPassword.notMatch");
    }
    return "";
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: IFormErrors = {
      password: validatePassword(formData.password),
      confirmPassword: validateConfirmPassword(
        formData.confirmPassword,
        formData.password
      ),
    };

    setErrors(newErrors);
    return (
      !newErrors.password &&
      !newErrors.confirmPassword &&
      formData.code.trim() !== ""
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (touched) {
      if (name === "password") {
        const error = validatePassword(value);
        setErrors((prev) => ({ ...prev, password: error }));
        if (formData.confirmPassword) {
          const confirmError = validateConfirmPassword(
            formData.confirmPassword,
            value
          );
          setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
        }
      } else if (name === "confirmPassword") {
        const error = validateConfirmPassword(value, formData.password);
        setErrors((prev) => ({ ...prev, confirmPassword: error }));
      }
    }
  };

  const handleSubmit = async () => {
    setTouched(true);

    if (!formData.code.trim()) {
      notify("error", t("resetPasswordForm.resetCodeMissing"));
      return;
    }

    try {
      if (validateForm()) {
        setLoading(true);

        // Call reset password API
        const resetResponse = await dispatch(
          handleResetPassword(formData)
        ).unwrap();
        if (resetResponse.status === "success") {
          const savedEmail = localStorage.getItem("reset_password_email");

          if (savedEmail) {
            try {
              await dispatch(
                handleLogin({
                  email: savedEmail,
                  password: formData.password,
                })
              ).unwrap();

              localStorage.removeItem("reset_password_email");

              navigate(adminRouteAbsolute.dashboard, { replace: true });
            } catch (loginError) {
              notify("success", t("resetPasswordForm.successMessage"));
              navigate(adminRouteAbsolute.login, { replace: true });
            }
          } else {
            notify("success", t("resetPasswordForm.successMessage"));
            navigate(adminRouteAbsolute.login, { replace: true });
          }
        }
      }
    } catch (e: any) {
      const errorMessage =
        e?.response?.data?.message || t("resetPasswordForm.errorMessage");
      notify("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  const handleBackToLogin = () => {
    navigate(adminRouteAbsolute.login);
  };

  return (
    <div className={cx("reset-password-form-container")}>
      <OutlinedButton
        title={t("backToLogin")}
        icon={<ArrowLeftIcon className={cx("back-to-login-icon")} />}
        onClick={handleBackToLogin}
        className={cx("back-to-login-button")}
      />
      <div className={cx("reset-password-form-content")}>
        <div className={cx("reset-password-label")}>
          <h3 className={cx("title")}>{t("resetPasswordForm.title")}</h3>
          <p className={cx("description")}>
            {t("resetPasswordForm.description")}
          </p>
        </div>

        <form className={cx("reset-password-form")} onSubmit={handleFormSubmit}>
          <PasswordInput
            name="password"
            label={t("password.label")}
            placeholder={t("password.placeholder")}
            value={formData.password}
            onChange={handleInputChange}
            errorMessage={errors.password}
          />
          <PasswordInput
            name="confirmPassword"
            label={t("confirmPassword.label")}
            placeholder={t("confirmPassword.placeholder")}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            errorMessage={errors.confirmPassword}
          />

          <div className={cx("reset-password-form-actions")}>
            <PrimaryButton
              title={t("resetPasswordButton")}
              className={cx("reset-password-button")}
              loading={loading}
              type="submit"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordForm;
